import React from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Trash2 } from 'lucide-react'
import { useFlowNotes } from '@/contexts/FlowNotesProvider'
import { useMeetingAppContext } from '@/MeetingAppContextDef'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { debateToolModes } from '@/utils/common'
import { FlowGrid } from './FlowGrid'
import { useHall } from '@/contexts/HallProvider'
import { createInitialColumns, parseDebateFormat } from '@db8/shared'

export const FlowNotesModal: React.FC = () => {
  const { hall, hallMetadata } = useHall()
  const { isDebator, isModerator } = useHallUsers()
  const { setDebateToolMode, debateToolMode } = useMeetingAppContext()
  const { pages, currentPageId, setCurrentPageId, addPage, removePage, updatePage } = useFlowNotes()

  const currentPage = pages.find((p) => p.id === currentPageId)

  const handleAddPage = (side: 'AFF' | 'NEG') => {
    const newId = `page-${Date.now()}`
    const parsedFormat = parseDebateFormat(hall.format)
    const columns = parsedFormat ? createInitialColumns(parsedFormat, hallMetadata?.turns) : []

    addPage({
      id: newId,
      name: side === 'AFF' ? 'New AFF Page' : 'New NEG Page',
      side,
      columns,
    })

    setCurrentPageId(newId)
  }

  return (
    <Dialog
      open={debateToolMode === debateToolModes.FLOWNOTES}
      onOpenChange={(open) => {
        if (!open) setDebateToolMode(false)
      }}
    >
      <DialogContent className="w-full h-full p-0 m-0 dark:text-white" size="full">
        <div className="w-full flex flex-col sm:flex-row h-full overflow-hidden">
          {(isDebator || isModerator) && (
            <div className="flex-shrink-0 sm:min-w-[250px] w-full sm:w-1/6 border-r border-faint h-[200px] sm:h-full overflow-y-auto">
              <h2 className="text-lg font-bold text-center py-2">Flow Notes</h2>
              <div className="p-3 flex gap-2 border-b border-faint px-4 md:px-2">
                <Button
                  variant="marketing"
                  className="gradient-bg-affirmative text-white font-semibold flex-1"
                  onClick={() => handleAddPage('AFF')}
                >
                  + AFF
                </Button>
                <Button
                  variant="marketing"
                  className="gradient-bg-negative text-white font-semibold flex-1"
                  onClick={() => handleAddPage('NEG')}
                >
                  + NEG
                </Button>
              </div>
              <div className="flex flex-col px-2 md:px-0">
                {pages.map((page) => {
                  const pageColor =
                    page.side === 'AFF'
                      ? 'gradient-bg-affirmative'
                      : page.side === 'NEG'
                        ? 'gradient-bg-negative'
                        : 'ai-gradient'
                  const isSelected = page.id === currentPageId
                  return (
                    <div key={page.id} className="flex justify-center w-full mt-2 mb-1 px-2">
                      <div
                        onClick={() => setCurrentPageId(page.id)}
                        className={`
                    p-2 cursor-pointer w-full
                    ${pageColor}
                    ${isSelected ? 'opacity-90' : 'opacity-40'}
                    hover:opacity-100
                    transition
                    rounded
                    text-white font-semibold
                  `}
                      >
                        {page.name}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex items-center border-b border-faint p-2 px-4 pr-[75px]">
              <Input
                className="flex-1 dark:text-white bg-gray-100 dark:border-none focus:ring-0 focus:outline-none h-12 "
                placeholder="Type name here"
                value={currentPage?.name ?? ''}
                onChange={(e) =>
                  currentPage && updatePage(currentPage.id, { name: e.target.value })
                }
                maxLength={50}
              />
              {currentPage?.side !== 'AI' && (
                <Button
                  variant="ghost"
                  title="Remove Page"
                  className="rounded p-1 dark:text-white hover:bg-gray-300 hover:text-red-600 bg-gray-300 dark:bg-gray-900 ml-2  w-[40px] h-[40px]"
                  onClick={() => removePage(currentPage.id)}
                >
                  <Trash2 size={15} />
                </Button>
              )}
            </div>

            <div className="w-full h-full overflow-x-auto">
              <div className="flex h-full min-w-full">
                <FlowGrid />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
